import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface TypewriterHeadlineProps {
  text: string;
  className?: string;
  cursorClassName?: string;
  typeSpeed?: number;
  startDelay?: number;
  showCursor?: "always" | "typing" | "never";
  highlightText?: string;
  as?: "h1" | "h2" | "h3" | "p" | "span";
}

export const TypewriterHeadline = ({
  text,
  className,
  cursorClassName,
  typeSpeed = 50,
  startDelay = 0,
  showCursor = "always",
  highlightText,
  as: Component = "h1",
}: TypewriterHeadlineProps) => {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const charCount = text.length;
  const typeDuration = charCount * typeSpeed;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), startDelay);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "-50px" }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [startDelay]);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsTypingComplete(true);
      }, typeDuration + 100);
      return () => clearTimeout(timer);
    }
  }, [isVisible, typeDuration]);

  // Split text into regular and highlighted parts
  const renderText = () => {
    if (!highlightText) {
      return <span>{text}</span>;
    }

    const index = text.indexOf(highlightText);
    if (index === -1) {
      return <span>{text}</span>;
    }

    const before = text.slice(0, index);
    const after = text.slice(index + highlightText.length);

    return (
      <>
        <span>{before}</span>
        <span className="text-gradient">{highlightText}</span>
        <span>{after}</span>
      </>
    );
  };

  const shouldShowCursor =
    showCursor === "always" ||
    (showCursor === "typing" && isVisible && !isTypingComplete);

  return (
    <Component
      ref={ref as React.RefObject<HTMLHeadingElement>}
      className={cn("relative inline-block", className)}
      style={
        {
          "--char-count": charCount,
          "--type-duration": `${typeDuration}ms`,
        } as React.CSSProperties
      }
    >
      <span
        className={cn(
          "typewriter-text",
          isVisible && "typewriter-animate"
        )}
      >
        {renderText()}
      </span>
      {shouldShowCursor && (
        <span
          className={cn(
            "typewriter-cursor pointer-events-none",
            isTypingComplete && showCursor === "typing" && "typewriter-cursor-fade",
            cursorClassName
          )}
          aria-hidden="true"
        >
          _
        </span>
      )}
    </Component>
  );
};

export default TypewriterHeadline;
